<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import InvoiceProcessingResults, {
  type InvoiceProcessingResultsInvoice,
} from './InvoiceProcessingResults.vue';

type FilterStatus = 'approved' | 'verified-debt';

const { t } = useI18n();

export interface FailedItem {
  id: string;
  label: string;
  errorMessages: string;
}

const paymentDate = defineModel<string>('paymentDate', { default: '' });

export type InvoiceProcessingPanelInvoice = InvoiceProcessingResultsInvoice;
defineProps<{
  activeFilter: FilterStatus;
  isProcessing: boolean;
  isProcessingDone: boolean;
  isRetrying: boolean;
  retryingIds: Set<string>;
  isDownloading: boolean;
  isPaymentFileDownloaded: boolean;
  isPaymentConfirmed: boolean;
  processedCount: number;
  selectedCount: number;
  failedItems: FailedItem[];
  succeededInvoices: InvoiceProcessingPanelInvoice[];
  failedInvoices: InvoiceProcessingPanelInvoice[];
}>();

const emit = defineEmits<{
  (e: 'process'): void;
  (e: 'retryOne', id: string): void;
  (e: 'downloadFile'): void;
  (e: 'confirmPayment'): void;
  (e: 'clearAllErrors'): void;
  (e: 'reloadAll'): void;
  (e: 'proceedToNextStage'): void;
}>();
</script>

<template>
  <!--
    NOTE: approved invoices
    INFO:
    In this step we verify the debt of the selected invoices
    On success, an invoice is set to booked=true and a voucher is created with the corresponding invoice id
  -->
  <div
    v-if="activeFilter === 'approved' && !isProcessing"
    class="action-panel panel-approved"
  >
    <div class="action-panel-content">
      <div class="action-info">
        <span class="action-label">
          {{ t('adminSupplierInvoice.bookingInvoices.verifyAndBookTitle') }}
        </span>
        <span class="action-hint">
          {{ selectedCount }}
          {{ t('adminSupplierInvoice.bookingInvoices.chosen') }}
        </span>
      </div>
      <button
        class="btn-process"
        :disabled="selectedCount === 0 || isProcessingDone"
        @click="emit('process')"
      >
        {{ t('adminSupplierInvoice.bookingInvoices.verifyAndBook') }}
      </button>
    </div>
  </div>

  <!--
    NOTE: Verified-debt panel
    INFO:
    In this step we download the payment file
    Additionally, a paymentFileId is put on a SupplierInvoice
  -->
  <div
    v-else-if="
      activeFilter === 'verified-debt' &&
      !isProcessing &&
      !isPaymentFileDownloaded
    "
    class="action-panel panel-verified-debt"
  >
    <div class="action-panel-content">
      <div class="action-info">
        <span class="action-label">
          {{
            t('adminSupplierInvoice.bookingInvoices.downloadPaymentFileTitle')
          }}
        </span>
        <span class="action-hint">
          {{ selectedCount }}
          {{ t('adminSupplierInvoice.bookingInvoices.chosen') }}
        </span>
      </div>
      <div class="date-and-download">
        <div class="date-picker">
          <label for="date">
            {{ t('adminSupplierInvoice.bookingInvoices.setPayDate') }}
          </label>
          <input
            class="compact-select"
            v-model="paymentDate"
            name="date"
            type="date"
            :min="new Date().toISOString().split('T')[0]"
            required
            placeholder="-"
          />
        </div>
        <button
          class="btn-process"
          :disabled="selectedCount === 0 || isDownloading || !paymentDate"
          @click="emit('downloadFile')"
        >
          <span v-if="isDownloading" class="spinner" />
          {{
            isDownloading
              ? t('adminSupplierInvoice.bookingInvoices.downloading')
              : t('adminSupplierInvoice.bookingInvoices.downloadPaymentFile')
          }}
        </button>
      </div>
    </div>
  </div>

  <!--
    NOTE: Verified-debt panel
    INFO:
    In this step we confirm that the downloaded payment file has been used
    The paymentFile is submitted to the backend, which in turn will set a
    finalPayDate on each invoice and update its status to 'paid'
  -->
  <div
    v-else-if="
      activeFilter === 'verified-debt' &&
      !isProcessing &&
      isPaymentFileDownloaded &&
      !isPaymentConfirmed
    "
    class="action-panel panel-verified-debt panel-confirm"
  >
    <div class="action-panel-content">
      <div class="action-info">
        <span class="action-label">
          {{ t('adminSupplierInvoice.bookingInvoices.confirmPaymentTitle') }}
        </span>
        <span v-if="!isPaymentConfirmed" class="action-hint pay-hint">
          {{ t('adminSupplierInvoice.bookingInvoices.confirmPaymentHint') }}
        </span>
        <span v-else-if="failedItems.length > 0" class="action-hint pay-hint">
          {{ t('adminSupplierInvoice.bookingInvoices.confirmPaymentError') }}
        </span>
        <span v-else class="action-hint pay-hint">
          {{
            t('adminSupplierInvoice.bookingInvoices.confirmPaymentFileSaved')
          }}
        </span>
      </div>
      <button
        v-if="!isPaymentConfirmed"
        class="btn-process btn-confirm"
        @click="emit('confirmPayment')"
      >
        {{ t('adminSupplierInvoice.bookingInvoices.confirmPayment') }}
      </button>
      <button v-else class="btn-process" @click="emit('reloadAll')">
        {{ t('adminSupplierInvoice.bookingInvoices.return') }}
      </button>
    </div>
  </div>

  <!--
    NOTE: Verified-debt panel
    INFO:
    In this step, we have previously confirmed the payment file to have been used. It is time to bookkeep.
    The payment vouchers are being created. Since there now are debt vouchers on each invoice, and there
    also is a finalPayDate on each invoice, the createVoucher will take the next necessary step and bookkeep
    the payment of the invoices.
  -->
  <div
    v-else-if="
      activeFilter === 'verified-debt' && !isProcessing && isPaymentConfirmed
    "
    class="action-panel panel-verified-debt panel-verify"
  >
    <div class="action-panel-content">
      <div class="action-info">
        <span class="action-label">
          {{ t('adminSupplierInvoice.bookingInvoices.payAndBookTitle') }}
        </span>
        <span class="action-hint">
          {{ selectedCount }}
          {{ t('adminSupplierInvoice.bookingInvoices.chosen') }}
        </span>
      </div>
      <button
        class="btn-process"
        :disabled="selectedCount === 0 || isProcessingDone"
        @click="emit('process')"
      >
        {{ t('adminSupplierInvoice.bookingInvoices.payAndBook') }}
      </button>
    </div>
  </div>

  <InvoiceProcessingResults
    v-if="!isProcessing && processedCount > 0"
    :processedCount="processedCount"
    :failedItems="failedItems"
    :failedInvoices="failedInvoices"
    :succeededInvoices="succeededInvoices"
    :retryingIds="retryingIds"
    :isRetrying="isRetrying"
    @retryOne="emit('retryOne', $event)"
    @clearAllErrors="emit('clearAllErrors')"
    @proceedToNextStage="emit('proceedToNextStage')"
  />
</template>

<style scoped lang="scss">
@use '@/assets/style/variables';
@use '@/assets/style/invoice';
@use 'bulma/sass/utilities/initial-variables' as *;

.action-panel {
  margin-top: 16px;
  border-radius: 8px;
  padding: 14px 18px;
  border: 1px solid #ddd;

  &.panel-approved {
    background-color: var(--invoice-approved);
    border-color: var(--table-mint);
  }

  &.panel-verified-debt {
    background-color: var(--invoice-undercertificate);
    border-color: var(--invoice-paid);
  }

  &.panel-confirm {
    border-color: var(--primary-color);
    border-width: 2px;
  }

  &.panel-verify {
    background-color: var(--table-orange);
    border-color: var(--table-red);
  }
}

.action-panel-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;

  .date-and-download {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1rem;

    .date-picker {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
  }
}

.action-info {
  display: flex;
  flex-direction: column;

  .action-label {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--primary-color);
  }

  .action-hint {
    font-size: 0.8rem;
    color: var(--inactive-color);
    &.pay-hint {
      font-weight: bold;
      font-family: sans-serif;
      font-size: large;
      color: var(--table-orange);
      text-shadow:
        -1px -1px 0 var(--primary-color),
        1px -1px 0 var(--primary-color),
        -1px 1px 0 var(--primary-color),
        1px 1px 0 var(--primary-color);
    }
  }
}

.btn-process {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 7px 18px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  background-color: var(--primary-color);
  color: white;
  border: 1px solid transparent;
  transition: all 0.2s;

  &:not(:disabled):hover {
    box-shadow: 0 0 0 1.5px white inset;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &.btn-confirm {
    &:not(:disabled):hover {
      box-shadow: 0 0 0 1.5px white inset;
    }
  }
}

// download button spinner!
.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
